import React from 'react';
import { JcBox } from './JcBox';
import { JcCommonProps, JcUtils } from '../utils/JcUtils';

export interface JcResponsiveGridProps extends JcCommonProps {
    /** Minimum width (px) an item may shrink to before the grid drops a
     *  column. Defaults to 380. */
    readonly minItemWidth?: number;
    /** Add the standard gap between items (mirrors JcBox `spacing`). */
    readonly spacing?: boolean;
    /** ARIA role when the grid is a semantic unit (e.g. "list" of cards). */
    readonly role?: React.AriaRole;
    /** Accessible name for the grid; pair with `role`. */
    readonly ariaLabel?: string;
    readonly children: React.ReactNode;
}

type JcCssVars = React.CSSProperties & Record<`--${string}`, string | number>;

/**
 * Lays out any number of same-shaped items in equal-width columns that
 * auto-fit the available width — one per row once a container can't fit two
 * at `minItemWidth`, more columns as space allows. Pure CSS grid, no
 * breakpoints or JS.
 */
export const JcResponsiveGrid = (
    props: JcResponsiveGridProps,
): React.ReactElement => {
    const minItemWidth = props.minItemWidth ?? 380;
    const commonProps = JcUtils.commonProps(props, ['jc-responsive-grid']);

    const style: JcCssVars = {
        ...props.style,
        '--jc-responsive-grid-min-item-width': `${minItemWidth}px`,
    };

    return (
        <JcBox {...commonProps} style={style} spacing={props.spacing} role={props.role} ariaLabel={props.ariaLabel}>
            {props.children}
        </JcBox>
    );
};
